import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './Credencial.css';
import {
  consultarCredencialTemporal,
  consultarMedicard,
  consultarMedicardAfiliado,
  type AfiliadoSeleccion,
} from '@lib/services/credencial.service';

/**
 * Página pública `/credencial`: un socio consulta el número de su credencial
 * digital (Medicard) ingresando el DNI. Pega al proxy `@Public`
 * `GET /credencial/medicard?doc=` (que devuelve SOLO el número). Diseño provisto
 * por el equipo de diseño (snippet ".mcard", ver Credencial.css); hereda la
 * fuente de marca Satoshi del body de la app.
 */

const LINK_WEB = 'https://app.medicus.com.ar/#/';
const LINK_ANDROID = 'https://play.google.com/store/apps/details?id=com.medicus.mimedicus&hl=es_AR';
const LINK_IOS = 'https://apps.apple.com/ar/app/mi-medicus/id1509030938';
const TEL_0800 = '0800-220-9006';

type Plataforma = 'ios' | 'android' | 'web';

type Plan = 'MS1' | 'MS2' | 'otro' | null;

type Resultado =
  | { tipo: 'encontrado'; medicard: string; nombre: string; plan: Plan }
  | { tipo: 'no-encontrado' }
  | { tipo: 'multiple'; afiliados?: AfiliadoSeleccion[] }
  | { tipo: 'error'; status?: number };

/* ───────────────────────── iconos (SVG del diseño) ───────────────────────── */

function IcoCard() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <circle cx="9" cy="12" r="2.5" />
      <path d="M15 10h2M15 14h2" />
    </svg>
  );
}

function IcoArrow({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IcoSpinner() {
  return (
    <svg className="mcard-spinner" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2.5} opacity={0.25} />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" />
    </svg>
  );
}

function IcoCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function IcoError() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function IcoCopy() {
  return (
    <svg className="mcard-copy-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IcoCopyCheck() {
  return (
    <svg className="mcard-copy-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function IcoBack() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IcoClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

function IcoLock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IcoChevron() {
  return (
    <svg className="mcard-person-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/* ──────────────────────────────── helpers ───────────────────────────────── */

function detectarPlataforma(): Plataforma {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'web';
}

function linkApp(p: Plataforma): string {
  if (p === 'ios') return LINK_IOS;
  if (p === 'android') return LINK_ANDROID;
  return LINK_WEB;
}

function validarDni(v: string): string {
  if (!v) return 'Ingresá tu número de DNI.';
  if (!/^\d+$/.test(v)) return 'El DNI debe contener solo números.';
  if (v.length < 7 || v.length > 8) return 'El DNI debe tener 7 u 8 dígitos.';
  return '';
}

function mensajeFalla(r: Resultado): { label: string; sub: string } {
  if (r.tipo === 'error') {
    const sub =
      r.status === 429
        ? 'Hiciste muchas consultas seguidas. Esperá un momento e intentá de nuevo.'
        : 'Hubo un problema de conexión. Reintentá en unos minutos.';
    return { label: 'No pudimos completar la consulta.', sub };
  }
  if (r.tipo === 'multiple') {
    return {
      label: 'Encontramos más de un resultado.',
      sub: `Si ninguno sos vos o no podés continuar, comunicate al ${TEL_0800}.`,
    };
  }
  return {
    label: 'No encontramos un registro asociado a ese DNI.',
    sub: `Verificá que el número sea correcto. Si el problema persiste, comunicate al ${TEL_0800}.`,
  };
}

function statusDeError(err: unknown): number | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/** ISO `YYYY-MM-DD` → `DD/MM/YYYY` para la lista de DNI compartido. */
function fmtFecha(iso?: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Iniciales (primera + última palabra) para el avatar de la selección. */
function inicialesDe(nombre: string): string {
  const parts = (nombre || '').trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (a + b).toUpperCase() || '·';
}

/**
 * Fallback de copiado para contextos donde la Clipboard API no está disponible
 * o está bloqueada — caso típico: la página embebida en un iframe cross-origin
 * (Webflow) sin `allow="clipboard-write"`, donde `navigator.clipboard` rechaza.
 * Usa un textarea oculto + `execCommand('copy')`, que sí funciona dentro de un
 * iframe sobre el gesto del usuario. Devuelve true si copió.
 */
function copiarLegacy(texto: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = texto;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

/** Segundos → `m:ss` (ej. 127 → "2:07"), formato del token de la credencial. */
function fmtMmSs(s: number): string {
  const seg = Math.max(0, s);
  const m = Math.floor(seg / 60);
  const ss = seg % 60;
  return `${m}:${ss < 10 ? '0' : ''}${ss}`;
}

/** String que codifica el QR de la credencial, mismo formato que el visor
 *  oficial: `medicard|token|0,00000|0,00000|0` (lat/long en 0, flag 0). */
function qrStringDe(medicard: string, token: string): string {
  return `${medicard}|${token}|0,00000|0,00000|0`;
}

/* ─────────────────────────────── subvistas ──────────────────────────────── */

/** Pie del resultado: reintentar la consulta y, si hay credencial (éxito),
 *  acceso a la credencial temporal. En el caso de error/no-encontrado no se
 *  ofrece la temporal (no hay medicard del cual generarla). */
function ResultadoFoot({ onReset, onTemp }: { onReset: () => void; onTemp?: () => void }) {
  return (
    <div className="mcard-foot">
      <button type="button" className="mcard-retry" onClick={onReset}>
        Buscar otro DNI
      </button>
      {onTemp && (
        <button type="button" className="mcard-temp-btn" onClick={onTemp}>
          Credencial temporal
          <IcoArrow className="mcard-temp-go" />
        </button>
      )}
    </div>
  );
}

interface ExitoProps {
  medicard: string;
  plan: Plan;
  copiado: boolean;
  onCopiar: () => void;
  onReset: () => void;
  onTemp: () => void;
  onCotizar: () => void;
}
function ResultadoExito({ medicard, plan, copiado, onCopiar, onReset, onTemp, onCotizar }: ExitoProps) {
  const planLabel = plan === 'MS1' ? 'Plan MS1' : plan === 'MS2' ? 'Plan MS2' : null;
  return (
    <div className="mcard-result">
      <div className="mcard-success">
        <div className="mcard-success-top">
          <span className="mcard-result-ico mcard-result-ico--ok" aria-hidden="true">
            <IcoCheck />
          </span>
          {planLabel && <span className="mcard-plan-badge">{planLabel}</span>}
        </div>
        <div className="mcard-result-numrow">
          <span className="mcard-result-number">{medicard}</span>
          <button
            type="button"
            className={copiado ? 'mcard-copy is-copied' : 'mcard-copy'}
            title="Copiar número"
            aria-label="Copiar número"
            onClick={onCopiar}
          >
            {copiado ? <IcoCopyCheck /> : <IcoCopy />}
          </button>
        </div>
      </div>
      {/* Solo MS1 ve "Cotizar Plan MS2" → deep-link a /mejora-plan (Figma). */}
      {plan === 'MS1' && (
        <div className="mcard-cta-wrap">
          <button type="button" className="mcard-cta" onClick={onCotizar}>
            Cotizar Plan MS2
            <IcoArrow />
          </button>
          <p className="mcard-cta-hint">Accedé a más beneficios con el plan superador</p>
        </div>
      )}
      <ResultadoFoot onReset={onReset} onTemp={onTemp} />
    </div>
  );
}

function ResultadoFalla({ resultado, onReset }: { resultado: Resultado; onReset: () => void }) {
  const { label, sub } = mensajeFalla(resultado);
  return (
    <div className="mcard-result">
      <div className="mcard-notfound">
        <span className="mcard-result-ico mcard-result-ico--err" aria-hidden="true">
          <IcoError />
        </span>
        <div className="mcard-result-body">
          <span className="mcard-result-label">{label}</span>
          <span className="mcard-result-sub">{sub}</span>
        </div>
      </div>
      <ResultadoFoot onReset={onReset} />
    </div>
  );
}

/** Figma "estado-dni-duplicado-seleccion" (node 202:393): el DNI matchea varias
 *  personas → el socio elige cuál es. La lista NO trae el Medicard (se pide al
 *  elegir, con el afiId). */
function SeleccionAfiliado({
  afiliados,
  cargando,
  onElegir,
  onReset,
}: {
  afiliados: AfiliadoSeleccion[];
  cargando: boolean;
  onElegir: (id: string) => void;
  onReset: () => void;
}) {
  return (
    <div className="mcard-select">
      <p className="mcard-select-sub">Seleccioná la opción que coincida con tus datos.</p>
      <div className="mcard-select-list">
        {afiliados.map((a) => (
          <button
            key={a.id}
            type="button"
            className="mcard-person"
            disabled={cargando}
            onClick={() => onElegir(a.id)}
          >
            <span className="mcard-person-avatar" aria-hidden="true">
              {inicialesDe(a.nombre)}
            </span>
            <span className="mcard-person-info">
              <span className="mcard-person-name">{a.nombre || 'Afiliado'}</span>
              {a.fechaNacimiento && (
                <span className="mcard-person-sub">Fecha de nac.: {fmtFecha(a.fechaNacimiento)}</span>
              )}
            </span>
            <IcoChevron />
          </button>
        ))}
      </div>
      <button type="button" className="mcard-retry" onClick={onReset}>
        Volver a ingresar DNI
      </button>
    </div>
  );
}

/** Figma "Estado Confirmación de Identidad" (node 179:4): "¿Sos NOMBRE?" antes
 *  de revelar el número de Medicard (el socio confirma su identidad). */
function ConfirmacionIdentidad({
  nombre,
  onSi,
  onNo,
}: {
  nombre: string;
  onSi: () => void;
  onNo: () => void;
}) {
  return (
    <div className="mcard-confirm">
      <p className="mcard-confirm-q">¿Sos {nombre || 'vos'}?</p>
      <p className="mcard-confirm-sub">Confirmá para ver tu número de Medicard.</p>
      <div className="mcard-confirm-actions">
        <button type="button" className="mcard-btn mcard-confirm-yes" onClick={onSi}>
          <IcoCheck />
          Sí, soy yo
        </button>
        <button type="button" className="mcard-confirm-no" onClick={onNo}>
          <IcoError />
          No soy yo
        </button>
      </div>
    </div>
  );
}

interface CredTemp {
  medicard: string;
  nombre: string;
  token: string;
}
type EstadoTemp = 'cargando' | 'ok' | 'no-encontrado' | 'error';

/** Tarjeta de la credencial digital temporal: medicard + nombre + QR + token
 *  rotativo de 4 dígitos con countdown. Pega a `GET /credencial/temporal?doc=`
 *  al abrir y RE-CONSULTA el token al expirar la ventana (cada 5 min), igual que
 *  el visor oficial. El token y el QR se renuevan juntos. */
function CredencialTemporal({ dni, onVolver }: { dni: string; onVolver: () => void }) {
  const [estado, setEstado] = useState<EstadoTemp>('cargando');
  const [cred, setCred] = useState<CredTemp | null>(null);
  const [restante, setRestante] = useState(0);
  // Timestamp absoluto de expiración del token vigente; el countdown lee de acá.
  const expiraRef = useRef(0);
  // Evita disparar varias re-consultas mientras una está en vuelo al expirar.
  const renovandoRef = useRef(false);

  const cargar = useCallback(async () => {
    try {
      const r = await consultarCredencialTemporal(dni);
      if (!r.encontrado) {
        setEstado('no-encontrado');
        return;
      }
      setCred({ medicard: r.medicard, nombre: r.nombre, token: r.token });
      expiraRef.current = Date.now() + r.time * 1000;
      setRestante(r.time);
      setEstado('ok');
    } catch {
      setEstado('error');
    }
  }, [dni]);

  // Carga inicial al abrir la credencial temporal.
  useEffect(() => {
    cargar().catch(() => setEstado('error'));
  }, [cargar]);

  // Countdown: un único intervalo mientras la credencial está visible. Lee el
  // timestamp de expiración (que `cargar` actualiza en cada renovación) y, al
  // llegar a 0, re-consulta el token (renueva token + QR + ventana). El guard
  // `renovandoRef` evita re-consultas duplicadas en el ~ida y vuelta de red.
  useEffect(() => {
    if (estado !== 'ok') return undefined;
    const tick = () => {
      const rem = Math.ceil((expiraRef.current - Date.now()) / 1000);
      if (rem <= 0) {
        setRestante(0);
        if (!renovandoRef.current) {
          renovandoRef.current = true;
          cargar().finally(() => {
            renovandoRef.current = false;
          });
        }
        return;
      }
      setRestante(rem);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [estado, cargar]);

  return (
    <div className="mcard-temp">
      {estado === 'cargando' && (
        <div className="mcard-tload">
          <IcoSpinner />
          <span>Generando tu credencial temporal…</span>
        </div>
      )}

      {estado === 'ok' && cred && (
        <div className="mcard-tcard-wrap">
          <div className="mcard-tbanner">
            <IcoClock />
            <span>
              Esta credencial es válida por <strong>24&nbsp;hs</strong>. Volvé a generarla cuando la
              necesites.
            </span>
          </div>

          <div className="mcred">
            <div className="mcred-num">{cred.medicard}</div>
            <div className="mcred-name">{cred.nombre}</div>
            <div className="mcred-divider" />
            <div className="mcred-bottom">
              <div className="mcred-qr">
                <QRCodeSVG
                  value={qrStringDe(cred.medicard, cred.token)}
                  size={116}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#101010"
                />
              </div>
              <div className="mcred-token">
                <span className="mcred-token-label">TOKEN</span>
                <span className="mcred-token-row">
                  <IcoLock />
                  <span>{cred.token}</span>
                </span>
                <span className="mcred-token-row">
                  <IcoClock />
                  <span>{fmtMmSs(restante)}</span>
                </span>
              </div>
            </div>
          </div>

          <p className="mcard-tnote">
            El <strong>token de seguridad</strong> es la clave de 4 dígitos que aparece junto al
            candado y que te pueden solicitar al recepcionarte en consultas o estudios. Se renueva
            automáticamente cada 5 minutos.
          </p>
        </div>
      )}

      {(estado === 'no-encontrado' || estado === 'error') && (
        <div className="mcard-notfound">
          <span className="mcard-result-ico mcard-result-ico--err" aria-hidden="true">
            <IcoError />
          </span>
          <div className="mcard-result-body">
            <span className="mcard-result-label">
              {estado === 'no-encontrado'
                ? 'No pudimos generar la credencial temporal para ese DNI.'
                : 'No pudimos generar la credencial en este momento.'}
            </span>
            <span className="mcard-result-sub">
              {estado === 'no-encontrado'
                ? `Si el problema persiste, comunicate al ${TEL_0800}.`
                : 'Reintentá en unos minutos.'}
            </span>
          </div>
        </div>
      )}

      <button type="button" className="mcard-temp-back" onClick={onVolver}>
        <IcoBack />
        Volver
      </button>
    </div>
  );
}

interface FormProps {
  dni: string;
  error: string;
  loading: boolean;
  inputKey: number;
  inputRef: React.RefObject<HTMLInputElement | null>;
  plataforma: Plataforma;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onConsultar: () => void;
}
function FormView({ dni, error, loading, inputKey, inputRef, plataforma, onChange, onKeyDown, onConsultar }: FormProps) {
  return (
    <>
      <div className="mcard-form">
        <div className="mcard-input-wrap">
          <input
            key={inputKey}
            ref={inputRef}
            type="text"
            inputMode="numeric"
            maxLength={8}
            className={error ? 'has-error' : undefined}
            placeholder="Ingresá tu DNI"
            aria-label="Número de DNI"
            autoComplete="off"
            value={dni}
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
          <span className={error ? 'mcard-error is-shown' : 'mcard-error'} aria-live="polite">
            {error}
          </span>
        </div>
        <button
          type="button"
          className={loading ? 'mcard-btn is-loading' : 'mcard-btn'}
          disabled={loading}
          onClick={onConsultar}
        >
          {loading ? (
            <IcoSpinner />
          ) : (
            <>
              <span className="mcard-btn-text">Consultar</span>
              <IcoArrow className="mcard-btn-arrow" />
            </>
          )}
        </button>
      </div>
      <div className="mcard-direct">
        <a href={linkApp(plataforma)} target="_blank" rel="noopener">
          Ya tengo mi número, <span className="da">ingresar directamente</span>
          <IcoArrow />
        </a>
      </div>
    </>
  );
}

/* ──────────────────────────────── página ────────────────────────────────── */

export function CredencialPage() {
  const [dni, setDni] = useState('');
  const [error, setError] = useState('');
  const [errorNonce, setErrorNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  // Identidad confirmada ("Sí, soy yo"): hasta entonces NO se muestra el número.
  const [confirmado, setConfirmado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [mostrarTemp, setMostrarTemp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const plataforma = useMemo(detectarPlataforma, []);
  // ¿La página corre embebida en un iframe de otro origen (Webflow, etc.)?
  const embebido = useMemo(() => globalThis.self !== globalThis.top, []);

  // Cada fallo de validación incrementa `errorNonce` → remonta el input
  // (cambio de `key`) para reiniciar la animación de "shake" y devolverle el foco.
  useEffect(() => {
    if (errorNonce > 0) inputRef.current?.focus();
  }, [errorNonce]);

  // Auto-resize del iframe: al estar embebida, la página le avisa su alto real
  // al documento padre por postMessage para que ajuste el iframe y no quede
  // scroll interno. El alto cambia según el estado (form/resultado/error) y en
  // mobile, por eso se observa con ResizeObserver además del aviso inicial. El
  // padre escucha `{ type: 'mcard:height', height }` (ver snippet de Webflow).
  useEffect(() => {
    if (!embebido) return;
    // Origen del documento host (el que nos embebe). El CSP frame-ancestors ya
    // restringe quién puede framear /credencial (webflow staging + medicus), así
    // que el referrer es uno de esos orígenes. Posteamos el alto a ESE origen
    // concreto, nunca a '*' (evita fuga cross-origin — Sonar S2819).
    let destino = '';
    try {
      destino = document.referrer ? new URL(document.referrer).origin : '';
    } catch {
      destino = '';
    }
    // El body pinta el fondo de marca (#f3f6fb); embebido lo dejamos
    // transparente para que se vea el fondo de la sección que hostea el iframe.
    const bodyBgPrevio = document.body.style.background;
    document.body.style.background = 'transparent';
    const avisarAlto = () => {
      if (!destino) return;
      const alto = Math.ceil(document.documentElement.scrollHeight);
      globalThis.parent.postMessage({ type: 'mcard:height', height: alto }, destino);
    };
    avisarAlto();
    const ro = new ResizeObserver(avisarAlto);
    ro.observe(document.body);
    globalThis.addEventListener('resize', avisarAlto);
    return () => {
      ro.disconnect();
      globalThis.removeEventListener('resize', avisarAlto);
      document.body.style.background = bodyBgPrevio;
    };
  }, [embebido]);

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replaceAll(/\D/g, '').slice(0, 8);
    setDni(v);
    if (error && !validarDni(v)) setError('');
  }

  async function ejecutarConsulta() {
    const v = dni.trim();
    const msg = validarDni(v);
    if (msg) {
      setError(msg);
      setErrorNonce((n) => n + 1);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await consultarMedicard(v);
      if (res.encontrado) {
        setConfirmado(false); // primero "¿Sos NOMBRE?", recién ahí el número
        setResultado({ tipo: 'encontrado', medicard: res.medicard, nombre: res.nombre, plan: res.plan });
      } else {
        setResultado(
          res.motivo === 'multiple'
            ? { tipo: 'multiple', afiliados: res.afiliados }
            : { tipo: 'no-encontrado' },
        );
      }
    } catch (err) {
      setResultado({ tipo: 'error', status: statusDeError(err) });
    } finally {
      setLoading(false);
    }
  }
  function consultar() {
    // ejecutarConsulta nunca rechaza (try/catch interno); el .catch evita el
    // floating-promise y no usamos `void` (rompe el Quality Gate del web).
    ejecutarConsulta().catch(() => undefined);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') consultar();
  }

  function copiar() {
    if (resultado?.tipo !== 'encontrado') return;
    const texto = resultado.medicard;
    const marcar = () => {
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    };
    // 1) Clipboard API (moderna). 2) Si no está o el iframe la bloquea, fallback
    // a execCommand. Así copia aunque falte `allow="clipboard-write"` en el host.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(texto).then(marcar, () => {
        if (copiarLegacy(texto)) marcar();
      });
      return;
    }
    if (copiarLegacy(texto)) marcar();
  }

  function reset() {
    setResultado(null);
    setConfirmado(false);
    setMostrarTemp(false);
    setDni('');
    setError('');
    setCopiado(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  // "Cotizar Plan MS2" → deep-link a /mejora-plan con el DNI precargado + el
  // origen ('credencial') para atribuir la conversión. Embebido (iframe) →
  // nueva pestaña; standalone → navega en la misma.
  function cotizar() {
    const url = `/mejora-plan?doc=${encodeURIComponent(dni)}&origen=credencial`;
    if (embebido) globalThis.open(url, '_blank', 'noopener');
    else globalThis.location.assign(url);
  }

  // DNI compartido: el socio eligió una persona → pedimos SU Medicard por afiId.
  // Como ya eligió su nombre de la lista, no re-pedimos confirmar identidad.
  function elegirAfiliado(afiId: string) {
    setLoading(true);
    consultarMedicardAfiliado(dni, afiId)
      .then((res) => {
        if (res.encontrado) {
          setConfirmado(true);
          setResultado({ tipo: 'encontrado', medicard: res.medicard, nombre: res.nombre, plan: res.plan });
        } else {
          setResultado({ tipo: 'no-encontrado' });
        }
      })
      .catch((err) => setResultado({ tipo: 'error', status: statusDeError(err) }))
      .finally(() => setLoading(false));
  }

  function tituloActual(): string {
    if (mostrarTemp) return 'Credencial digital temporal';
    if (resultado?.tipo === 'encontrado') {
      return confirmado ? 'Tu número de Medicard es:' : 'Confirmá tu identidad';
    }
    if (resultado?.tipo === 'multiple' && resultado.afiliados?.length) {
      return 'Encontramos más de un resultado';
    }
    return 'Consultá tu número de credencial digital';
  }

  function contenido() {
    if (mostrarTemp) {
      return <CredencialTemporal dni={dni} onVolver={() => setMostrarTemp(false)} />;
    }
    if (!resultado) {
      return (
        <FormView
          dni={dni}
          error={error}
          loading={loading}
          inputKey={errorNonce}
          inputRef={inputRef}
          plataforma={plataforma}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onConsultar={consultar}
        />
      );
    }
    if (resultado.tipo === 'encontrado') {
      if (!confirmado) {
        return (
          <ConfirmacionIdentidad
            nombre={resultado.nombre}
            onSi={() => setConfirmado(true)}
            onNo={reset}
          />
        );
      }
      return (
        <ResultadoExito
          medicard={resultado.medicard}
          plan={resultado.plan}
          copiado={copiado}
          onCopiar={copiar}
          onReset={reset}
          onTemp={() => setMostrarTemp(true)}
          onCotizar={cotizar}
        />
      );
    }
    if (resultado.tipo === 'multiple' && resultado.afiliados && resultado.afiliados.length > 0) {
      return (
        <SeleccionAfiliado
          afiliados={resultado.afiliados}
          cargando={loading}
          onElegir={elegirAfiliado}
          onReset={reset}
        />
      );
    }
    return <ResultadoFalla resultado={resultado} onReset={reset} />;
  }

  return (
    <div className={embebido ? 'mcard-page mcard-page--embed' : 'mcard-page'}>
      <div className="mcard-shell">
        <div className="mcard">
          <div className="mcard-box">
            <div className="mcard-head">
              <span className="mcard-ico" aria-hidden="true">
                <IcoCard />
              </span>
              <span className="mcard-label">{tituloActual()}</span>
            </div>
            {contenido()}
          </div>
        </div>
      </div>
    </div>
  );
}
